import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  PageHeader,
  Pagination,
  TableSkeleton,
  generateTableFromSchema,
  useTable,
  Tag,
  EditPanel,
} from '@zolteam/zol-admin-ui';

// Components
import CustomerFilters from '../../components/organisms/customerFilters/CustomerFilters';
import CustomerDetails from '../../components/organisms/customerDetails/CustomerDetails';
// Services
import { useGetCustomerListQuery } from '../../services/customers/customersQueries';

/**
 * customerList component.
 * @returns {JSX.Element}
 */
function CustomerList() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  // json schema
  const UserListSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Customer',
    properties: {
      firstname: {
        type: 'string',
        description: t('customerList.columns.firstname'),
        x_layout: '1/1/1/7',
      },
      lastname: {
        type: 'string',
        description: t('customerList.columns.lastname'),
        x_layout: '1/7/1/13',
      },
      email: {
        type: 'string',
        format: 'email',
        description: t('customerList.columns.email'),
        x_layout: '2/1/2/13',
      },
      phone: {
        type: 'string',
        description: t('customerList.columns.phone'),
        x_layout: '3/1/3/13',
      },
      company: {
        type: 'string',
        description: t('customerList.columns.company'),
        x_layout: '4/1/4/13',
      },
      status: {
        type: 'string',
        description: t('customerList.columns.status'),
        x_layout: '4/1/4/13',
      },
    },
    additionalProperties: false,
  };

  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || 1;
  const pageSize = searchParam.get('pageSize') || 10;
  const sort = searchParam.get('sort') || 'lastname';
  const order = searchParam.get('order') || 'asc';
  const search = searchParam.get('search') || '';
  const statusFilter = searchParam.get('status') || '';
  const brokerIds = searchParam.get('brokerIds') || '';
  // local state
  const [currentCustomer, setCurrentCustomer] = useState(null);

  const {
    handlePaginationChange,
    handleColumnSort,
  } = useTable({
    setParams:
    /**
     * Set the search params.
     * @param {object} params
     * @returns {void}
     */
    (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
    // @ts-ignore
    search,
    statusFilter,
    brokerIds,
    defaultSelectedRows: [],
  });

  // Services
  const {
    data: dataCustomerList,
    isLoading: isLoadingDataCustomerList,
    refetch,
  } = useGetCustomerListQuery(
    {
      pageNumber: Number(pageNumber),
      pageSize: Number(pageSize),
      order,
      sort,
      search,
      status: statusFilter,
      brokerIds,
    },
    { keepPreviousData: true },
  );

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('/customers/new');
  /**
   * Handle the click on a table row.
   * @param {any} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => setCurrentCustomer(row);

  /**
   * Handle the close of the panel.
   * @returns {void}
   */
  const handleClosePanel = () => {
    setCurrentCustomer(null);
    refetch();
  };

  /**
   * Handle search param change in filter
   * @param {string} key
   * @param {string} value
   */
  const handleSearchParamChange = (key, value) => {
    if (searchParam.get('pageNumber') !== '1') {
      setSearchParams((searchParams) => {
        searchParams.set('pageNumber', '1');
        return searchParams;
      });
    }

    if (value) {
      setSearchParams((searchParams) => {
        searchParams.set(key, value);
        return searchParams;
      });
    } else {
      setSearchParams((searchParams) => {
        searchParams.delete(key);
        return searchParams;
      });
    }
  };

  return (
    <Box direction="column" gap="s" className="padding-l">
      <PageHeader
        title={(
          <>
            <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
              {t('customerList.title')}
            </h1>
            {dataCustomerList?.data?.length && dataCustomerList?.total ? (
              <span className="font-color-neutral-500">
                {t('customerList.nbRecords', { count: dataCustomerList.total })}
              </span>
            ) : null}
          </>
        )}
        actions={(
          <Button icon="add" onClick={handleNewContentClick}>
            {t('customerList.actions.new')}
          </Button>
        )}
      />

      <EditPanel
        id="customerDetail"
        onClose={handleClosePanel}
        isOpen={!!currentCustomer}
      >
        {currentCustomer
          ? (
            <CustomerDetails
              customer={currentCustomer}
              handleSuccess={refetch}
              handleClosePanel={handleClosePanel}
            />
          ) : null}
      </EditPanel>

      <CustomerFilters
        searchParam={searchParam}
        handleSearchParamChange={handleSearchParamChange}
        statusFilter={statusFilter}
        customerFilter={search}
        brokerIds={brokerIds}
      />

      {isLoadingDataCustomerList
        ? <TableSkeleton />
        : generateTableFromSchema(
          {
            JSONSchema: UserListSchema,
            data: dataCustomerList?.data || [],
            sort: {
              order,
              active: sort,
              handleColumnSort,
            },
            navigate: {
              // @ts-ignore
              handleRowNavigate,
            },
            labelNoResult: t('customerList.noResult'),
            customColumns: [
              {
                key: 'status',
                description: '',
                sortable: false,
                position: 'end',
                renderHead: () => t('customerList.columns.status'),
                renderCell: (/** @type {Customer} */row) => (<Tag label={t(`customerStatus.${row.status}`)} />),
              },
            ],
          },
        )}

      {dataCustomerList?.data.length && dataCustomerList?.total ? (
        <Pagination
          recordsCount={dataCustomerList.total}
          pageNumber={+pageNumber}
          pageSize={+pageSize}
          onPageNumberChange={handlePaginationChange}
          perPageResultLabel={t('pagination.resultPerPage')}
        />
      ) : null}

    </Box>
  );
}

export default CustomerList;
